'use client';

import { QueryKeys } from '@/constants/query-keys';
import { movieShowtimesApiService } from '@/services/(frontend)/showtimes/movie.showtimes-api.service';
import { GetMovieShowtimesRequest } from '@/types/paged-requests/movie-paged-request';
import { MovieShowtimesResponse } from '@/types/showtimes';
import { useQuery } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

type UseMovieShowtimesParams = Omit<GetMovieShowtimesRequest, 'movieId'> & {
  enabled?: boolean;
  movieId: number | undefined;
};

export const useMovieShowtimes = (params: UseMovieShowtimesParams): ReturnType<typeof useQuery<MovieShowtimesResponse | null>> => {
  const { date, enabled = true, movieId, pageSize, zipCode } = params;
  const updateQueryStaleTime = useDynamicStaleTime();
  const dateKey = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : undefined;

  return useQuery<MovieShowtimesResponse | null>({
    enabled: enabled && !!movieId && !!zipCode,
    queryFn: async ({ signal }) => {
      const response = await movieShowtimesApiService.getShowtimes({ date, movieId: movieId as number, pageSize, zipCode }, signal);
      updateQueryStaleTime(QueryKeys.global.movieShowtimes(movieId, dateKey, zipCode, pageSize), response.headers);
      return response?.data ?? null;
    },
    queryKey: QueryKeys.global.movieShowtimes(movieId, dateKey, zipCode, pageSize),
  });
};

export const useMovieShowtimeDates = (
  movieId: number | undefined,
  zipCode?: string
): ReturnType<typeof useQuery<string[] | undefined>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const queryKey = QueryKeys.global.movieShowtimeDates(movieId, zipCode);

  return useQuery<string[] | undefined>({
    enabled: !!movieId && !!zipCode,
    queryFn: async ({ signal }) => {
      const response = await movieShowtimesApiService.getAvailableDates(movieId as number, zipCode, signal);
      updateQueryStaleTime(queryKey, response.headers);
      return response.data;
    },
    queryKey,
  });
};
